import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ClipboardList,
  RefreshCw,
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  User,
  LogIn,
  LogOut,
  Eye,
  Edit,
  Trash2,
  Shield,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleString('ru-RU');
};

const ActionBadge = ({ action }) => {
  const config = {
    login: { label: 'Вход', className: 'bg-emerald-500/20 text-emerald-400', icon: LogIn },
    logout: { label: 'Выход', className: 'bg-slate-500/20 text-slate-400', icon: LogOut },
    view_payment: { label: 'Просмотр платежа', className: 'bg-blue-500/20 text-blue-400', icon: Eye },
    create_payment_link: { label: 'Создание ссылки', className: 'bg-cyan-500/20 text-cyan-400', icon: FileText },
    refund: { label: 'Возврат', className: 'bg-amber-500/20 text-amber-400', icon: RefreshCw },
    create_user: { label: 'Создание пользователя', className: 'bg-emerald-500/20 text-emerald-400', icon: User },
    update_user: { label: 'Обновление пользователя', className: 'bg-blue-500/20 text-blue-400', icon: Edit },
    delete_user: { label: 'Удаление пользователя', className: 'bg-red-500/20 text-red-400', icon: Trash2 },
    view_report: { label: 'Просмотр отчёта', className: 'bg-purple-500/20 text-purple-400', icon: FileText },
    export_report: { label: 'Экспорт отчёта', className: 'bg-purple-500/20 text-purple-400', icon: Download },
    aml_review: { label: 'Проверка AML', className: 'bg-orange-500/20 text-orange-400', icon: Shield },
    settings_change: { label: 'Изменение настроек', className: 'bg-slate-500/20 text-slate-400', icon: Edit },
  };
  const c = config[action] || { label: action, className: 'bg-slate-500/20 text-slate-400', icon: ClipboardList };
  const Icon = c.icon;
  return (
    <Badge variant="outline" className={c.className}>
      <Icon className="w-3 h-3 mr-1" />
      {c.label}
    </Badge>
  );
};

const AuditPage = () => {
  const { hasPermission } = useAuth();
  const { t, language } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50, total: 0 });
  const [actionFilter, setActionFilter] = useState('all');
  const [search, setSearch] = useState('');

  const fetchLogs = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pagination.pageSize);
      if (actionFilter && actionFilter !== 'all') params.append('action', actionFilter);
      
      const response = await api.get(`/audit/logs?${params}`);
      setLogs(response.data.logs || []);
      setPagination({
        page: response.data.page,
        pageSize: response.data.page_size,
        total: response.data.total,
      });
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [actionFilter]);

  const handleExport = async () => {
    try {
      const response = await api.get('/export/audit-logs', {
        params: { action: actionFilter !== 'all' ? actionFilter : undefined },
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Экспорт завершён');
    } catch (err) {
      toast.error('Ошибка экспорта');
    }
  };

  return (
    <div className="space-y-6" data-testid="audit-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Аудит-логи</h1>
          <p className="text-slate-400 mt-1">История действий пользователей</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleExport}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <Download className="w-4 h-4 mr-1" />
            {t('common.export')}
          </Button>
          <Button
            onClick={() => fetchLogs(pagination.page)}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-60 bg-slate-700/50 border-slate-600 text-white">
            <SelectValue placeholder="Тип действия" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все действия</SelectItem>
            <SelectItem value="login">Вход</SelectItem>
            <SelectItem value="logout">Выход</SelectItem>
            <SelectItem value="view_payment">Просмотр платежа</SelectItem>
            <SelectItem value="create_payment_link">Создание ссылки</SelectItem>
            <SelectItem value="refund">Возврат</SelectItem>
            <SelectItem value="create_user">Создание пользователя</SelectItem>
            <SelectItem value="update_user">Обновление пользователя</SelectItem>
            <SelectItem value="delete_user">Удаление пользователя</SelectItem>
            <SelectItem value="aml_review">Проверка AML</SelectItem>
            <SelectItem value="settings_change">Изменение настроек</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-emerald-500" />
            Логи ({pagination.total})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <ClipboardList className="w-12 h-12 mb-4 opacity-50" />
              <p>Логов нет</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left p-4 text-sm font-medium text-slate-400">{t('common.date')}</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">{t('audit.user')}</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">{t('audit.action')}</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Ресурс</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">ID объекта</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">IP</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">{t('common.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr
                      key={log.id}
                      className="border-b border-slate-700/50 hover:bg-slate-700/30"
                    >
                      <td className="p-4 text-slate-300 text-sm">
                        {formatDate(log.timestamp)}
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="text-white text-sm">{log.user_name}</p>
                          <p className="text-slate-500 text-xs">{log.user_email}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <ActionBadge action={log.action} />
                      </td>
                      <td className="p-4 text-slate-300 text-sm">
                        {log.resource_type || '—'}
                      </td>
                      <td className="p-4 text-slate-400 text-sm font-mono">
                        {log.resource_id || '—'}
                      </td>
                      <td className="p-4 text-slate-400 text-sm font-mono">
                        {log.ip_address || '—'}
                      </td>
                      <td className="p-4">
                        <Badge
                          variant="outline"
                          className={log.success ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}
                        >
                          {log.success ? 'Успех' : 'Ошибка'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {Math.ceil(pagination.total / pagination.pageSize) > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-slate-700">
              <p className="text-sm text-slate-400">
                Страница {pagination.page} из {Math.ceil(pagination.total / pagination.pageSize)}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchLogs(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchLogs(pagination.page + 1)}
                  disabled={pagination.page >= Math.ceil(pagination.total / pagination.pageSize)}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditPage;
